import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  useTheme,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  TextField,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Forum as ForumIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Help as HelpIcon,
} from "@material-ui/icons";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { UsersFilter } from "../../../components/UsersFilter";
import { createChat, updateChat } from "../../../services/chats.api";
import { useStyles } from "../chatStyles";

export function ChatModal({
  open,
  chat,
  type,
  handleClose,
  handleLoadNewChat,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setTitle("");
    setUsers([]);
    setIsSaving(false);
    if (type === "edit") {
      const userList = chat.users.map((u) => ({
        id: u.user.id,
        name: u.user.name,
      }));
      setUsers(userList);
      setTitle(chat.title);
    }
  }, [chat, open, type]);

  const handleSave = async () => {
    // ✅ PREVENIR DOBLE CLIC
    if (isSaving) {
      console.log("⚠️ Guardado en progreso, ignorando clic duplicado");
      return;
    }

    try {
      if (!title.trim()) {
        alert("Por favor, ingresa un título para la conversación.");
        return;
      }

      if (!users || users.length === 0) {
        alert("Por favor, selecciona al menos un usuario para la conversación.");
        return;
      }

      setIsSaving(true);
      console.log("💾 Guardando conversación...", { title, users: users.length });

      if (type === "edit") {
        await updateChat(chat.id, {
          users,
          title,
        });
        console.log("✅ Conversación actualizada correctamente");
      } else {
        const { data } = await createChat({
          users,
          title,
        });
        console.log("✅ Conversación creada correctamente:", data.id);
        handleLoadNewChat(data);
      }
      handleClose();
    } catch (err) {
      console.error("❌ Error al guardar conversación:", err);
      alert("Error al guardar la conversación. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSaving(false);
    }
  };

  const removeUser = (userId) => {
    setUsers(users.filter(user => user.id !== userId));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="chat-dialog-title"
      aria-describedby="chat-dialog-description"
      maxWidth="md"
      fullWidth
      fullScreen={isNarrow}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
        },
      }}
    >
      <div className={classes.dialogTitle}>
        <ForumIcon style={{ marginRight: 8 }} />
        <Typography variant="h6">
          {type === "edit" ? "Editar Conversación" : "Nueva Conversación"}
        </Typography>
      </div>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          {/* ✅ SECCIÓN 1: INFORMACIÓN BÁSICA */}
          <Grid item xs={12}>
            <div className={classes.sectionTitle}>
              <InfoIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">Información Básica</Typography>
            </div>

            <Card className={classes.stepCard}>
              <CardContent style={{ backgroundColor: theme.palette.background.paper }}>
                <TextField
                  label="Título de la conversación"
                  placeholder="Ej: Reunión de equipo, Proyecto XYZ, etc."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  variant="outlined"
                  fullWidth
                  helperText="Ingresa un título descriptivo para identificar fácilmente esta conversación"
                  className={classes.textField}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* ✅ SECCIÓN 2: SELECCIÓN DE USUARIOS */}
          <Grid item xs={12}>
            <div className={classes.sectionTitle}>
              <PeopleIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">Participantes</Typography>
            </div>

            <Card className={classes.stepCard}>
              <CardContent style={{ backgroundColor: theme.palette.background.paper }}>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} gutterBottom>
                  Selecciona los usuarios que participarán en esta conversación:
                </Typography>

                <UsersFilter
                  onFiltered={(users) => setUsers(users)}
                  initialUsers={users}
                />

                {/* ✅ MOSTRAR USUARIOS SELECCIONADOS */}
                {users.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" style={{ color: theme.palette.text.primary }} gutterBottom>
                      Usuarios seleccionados ({users.length}):
                    </Typography>
                    <div className={classes.selectedUsers}>
                      {users.map((user) => (
                        <Chip
                          key={user.id}
                          label={user.name}
                          onDelete={() => removeUser(user.id)}
                          className={classes.userChip}
                          avatar={<Avatar>{user.name.charAt(0).toUpperCase()}</Avatar>}
                        />
                      ))}
                    </div>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* ✅ SECCIÓN 3: AYUDA CONTEXTUAL */}
          <Grid item xs={12}>
            <div className={classes.helpSection}>
              <div className={classes.sectionTitle}>
                <HelpIcon style={{ marginRight: 8 }} />
                <Typography variant="h6">¿Cómo funciona?</Typography>
              </div>

              <List dense>
                <ListItem className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.featureIcon}>
                      <CheckCircleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    className={classes.listItemText}
                    primary="Comunicación interna"
                    secondary="Este chat es privado y solo visible para los usuarios seleccionados"
                  />
                </ListItem>

                <ListItem className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.featureIcon}>
                      <MessageIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    className={classes.listItemText}
                    primary="Mensajes en tiempo real"
                    secondary="Los mensajes se sincronizan automáticamente entre todos los participantes"
                  />
                </ListItem>

                <ListItem className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.featureIcon}>
                      <PeopleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    className={classes.listItemText}
                    primary="Colaboración eficiente"
                    secondary="Ideal para coordinación de equipos, proyectos y comunicación interna"
                  />
                </ListItem>
              </List>
            </div>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        className={classes.dialogActionsResponsive}
        style={{
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
        }}
      >
        <Button
          onClick={handleClose}
          color="secondary"
          fullWidth={isNarrow}
          style={{ color: theme.palette.error.main }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          fullWidth={isNarrow}
          disabled={!title.trim() || users.length === 0 || isSaving}
        >
          {isSaving ? "Guardando..." : (type === "edit" ? "Actualizar" : "Crear Conversación")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChatModal;
